import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import type { SearchHookLocation } from "@repo/maps";
import Link from "next/link";
import { Fragment } from "react";
import slugify from "slugify";

export type BreadcrumbItemData = {
  name: string;
  pathSegment?: string;
};

export type LocationBreadcrumbsProps = {
  countryCode?: string;
  location?: SearchHookLocation;
  basePath?: string;
  initialItems?: BreadcrumbItemData[];
  fallbackItem?: BreadcrumbItemData;
  maxDepth?: number;
};

export function getBreadcrumbItems({
  countryCode,
  location,
  basePath = "/",
  initialItems = [],
  maxDepth,
}: LocationBreadcrumbsProps): BreadcrumbItemData[] | null {
  if (!countryCode || !location) {
    return initialItems.length > 0 ? initialItems : null;
  }

  const { country, district, region, place } = location.context;

  const finalCountryCode = countryCode.toLowerCase();

  const items: BreadcrumbItemData[] = [
    {
      name: country?.name ?? "Unknown",
      pathSegment: `${basePath}${finalCountryCode}`,
    },
  ];

  if (region && (maxDepth === undefined || maxDepth >= 2)) {
    const regionSlug = slugify(region.name, { lower: true });

    items.push({
      name: region.name,
      pathSegment: `${basePath}${finalCountryCode}/${regionSlug}`,
    });
  }

  if (region && district && (maxDepth === undefined || maxDepth >= 3)) {
    const regionSlug = slugify(region.name, { lower: true });
    const districtSlug = slugify(district.name, { lower: true });

    items.push({
      name: district.name,
      pathSegment: `${basePath}${finalCountryCode}/${regionSlug}/${districtSlug}`,
    });
  }

  if (place && (maxDepth === undefined || maxDepth >= 4)) {
    items.push({
      name: place.name,
    });
  }

  if (items.length === 0) {
    return initialItems.length > 0 ? initialItems : null;
  }

  return [...initialItems, ...items];
}

export function LocationBreadcrumbs({
  countryCode,
  location,
  basePath,
  initialItems,
  fallbackItem,
}: LocationBreadcrumbsProps) {
  const items = getBreadcrumbItems({
    countryCode,
    location,
    basePath,
    initialItems,
  });

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {items ? (
          items.map(({ name, pathSegment }, index) => (
            <Fragment key={index}>
              <BreadcrumbItem>
                {index < items.length - 1 && pathSegment ? (
                  <BreadcrumbLink asChild>
                    <Link href={pathSegment}>{name}</Link>
                  </BreadcrumbLink>
                ) : (
                  <BreadcrumbPage>{name}</BreadcrumbPage>
                )}
              </BreadcrumbItem>

              {index < items.length - 1 && <BreadcrumbSeparator />}
            </Fragment>
          ))
        ) : (
          <BreadcrumbItem>
            <BreadcrumbPage>{fallbackItem?.name ?? "Unknown"}</BreadcrumbPage>
          </BreadcrumbItem>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

LocationBreadcrumbs.displayName = "LocationBreadcrumbs";
